import { api } from './client';
import type { Page } from './catalogs';
import type { CancellableState } from './cancellation';

// ==================== Modernization ====================

export interface ModernizationItem {
  id?: string;
  line_number: number;
  description: string;
  amount: string;
  cost_account_code?: string;
}

export interface FixedAssetModernization {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  fixed_asset_id: string;
  primary_cost_before: string;
  accumulated_depreciation_before: string;
  total_capitalized: string;
  external_source?: string | null;
  items: ModernizationItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface FixedAssetModernizationCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  fixed_asset_id: string;
  comment?: string | null;
  items: ModernizationItem[];
}

const MOD_BASE = '/documents/fixed-asset-modernization';

export const modernization = {
  list: (params?: { organization_id?: string; fixed_asset_id?: string;
                    posted?: boolean; date_from?: string; date_to?: string; limit?: number }) =>
    api.get<Page<FixedAssetModernization>>(MOD_BASE, { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<FixedAssetModernization>(`${MOD_BASE}/${id}`).then((r) => r.data),
  update: (id: string, p: Partial<FixedAssetModernizationCreate>) =>
    api.patch<FixedAssetModernization>(`${MOD_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${MOD_BASE}/${id}`).then(() => undefined),
  create: (p: FixedAssetModernizationCreate) =>
    api.post<FixedAssetModernization>(MOD_BASE, p).then((r) => r.data),
  post: (id: string) =>
    api.post<FixedAssetModernization>(`${MOD_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<FixedAssetModernization>(`${MOD_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${MOD_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
};

// ==================== Internal Transfer ====================

export type AssetKind = 'FIXED_ASSET' | 'INTANGIBLE' | 'LOW_VALUE';

export interface InternalTransferItem {
  id?: string;
  line_number: number;
  asset_kind: AssetKind;
  fixed_asset_id?: string | null;
  intangible_asset_id?: string | null;
  low_value_asset_id?: string | null;
  from_department_id?: string | null;
  to_department_id?: string | null;
  from_employee_id?: string | null;
  to_employee_id?: string | null;
  item_reason?: string | null;
}

export interface AssetInternalTransfer {
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  transfer_reason: string;
  external_source?: string | null;
  items: InternalTransferItem[];
  created_at: string;
  updated_at: string;
  // Block 5.3c:
  state?: CancellableState;
  cancelled_at?: string | null;
  cancellation_reason?: string | null;
}

export interface AssetInternalTransferCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  transfer_reason: string;
  comment?: string | null;
  items: InternalTransferItem[];
}

const TR_BASE = '/documents/asset-internal-transfer';

export const internalTransfer = {
  list: (params?: { organization_id?: string; posted?: boolean; date_from?: string; date_to?: string; limit?: number }) =>
    api.get<Page<AssetInternalTransfer>>(TR_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<AssetInternalTransfer>(`${TR_BASE}/${id}`).then((r) => r.data),
  update: (id: string, p: Partial<AssetInternalTransferCreate>) =>
    api.patch<AssetInternalTransfer>(`${TR_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) => api.delete(`${TR_BASE}/${id}`).then(() => undefined),
  create: (p: AssetInternalTransferCreate) =>
    api.post<AssetInternalTransfer>(TR_BASE, p).then((r) => r.data),
  post: (id: string) =>
    api.post<AssetInternalTransfer>(`${TR_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<AssetInternalTransfer>(`${TR_BASE}/${id}/unpost`).then((r) => r.data),
  cancel: (id: string, reason: string) =>
    api.post(`${TR_BASE}/${id}/cancel`, { cancellation_reason: reason }).then((r) => r.data),
  printViewUrl: (id: string) => `/api/v1${TR_BASE}/${id}/print-view`,
};

// ==================== Useful Life PATCH ====================

export type AllowedStatusChange = 'OPERATING' | 'CONSERVATION' | 'REPAIR_LONG';

export interface UsefulLifeChangeRequest {
  new_useful_life_months: number;
  reason: string;
  effective_date: string;
}

export const usefulLife = {
  changeFixedAsset: (id: string, p: UsefulLifeChangeRequest) =>
    api.patch(`/refs/fixed-assets/${id}/useful-life`, p).then((r) => r.data),
  changeIntangibleAsset: (id: string, p: UsefulLifeChangeRequest) =>
    api.patch(`/refs/intangible-assets/${id}/useful-life`, p).then((r) => r.data),
};
